// ─────────────────────────────────────────────────────────────────────────────
// Data layer for the responder flow (POV: a company answering a questionnaire).
// Pure RPC — each function makes one HTTP call and returns the raw response.
// ─────────────────────────────────────────────────────────────────────────────

import { QUESTIONNAIRE_SERVICE_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  CompanyDataPointAnswer,
  RawAnswerRequest,
  SaveAnswerItem,
  SubmitForReviewPayload,
} from './types';

export async function fetchAnswerRequest(
  id: string,
): Promise<RawAnswerRequest> {
  return apiFetch<RawAnswerRequest>(
    `/v1/questionnaires/requests/${id}`,
    {},
    QUESTIONNAIRE_SERVICE_URL,
  );
}

export async function postResponses(body: {
  payload: SaveAnswerItem[];
}): Promise<unknown> {
  return apiFetch<unknown>(
    '/v1/responses',
    { method: 'POST', body: JSON.stringify(body) },
    QUESTIONNAIRE_SERVICE_URL,
  );
}

export async function patchRequestStatus(
  id: string,
  body: SubmitForReviewPayload,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/v1/questionnaires/requests/${id}/status`,
    { method: 'PATCH', body: JSON.stringify(body) },
    QUESTIONNAIRE_SERVICE_URL,
  );
}

/** Returns the saved company-profile value for a given datapoint,
 *  scoped to a company. Used by the auto-fill-from-profile flow on the
 *  answer page: one call per datapoint-bound question, then the
 *  responses are POSTed via `postResponses` as `autoAnswered: true`. */
export async function fetchCompanyDataPointAnswer(
  dataPointId: string,
  companyId: string,
): Promise<CompanyDataPointAnswer> {
  return apiFetch<CompanyDataPointAnswer>(
    `/v1/data-points/${dataPointId}/companies/${companyId}`,
    {},
    QUESTIONNAIRE_SERVICE_URL,
  );
}

/** Reviewer-side per-answer status update. Drives the per-question
 *  review badge (APPROVED / DENIED / IN_REVIEW / NOT_SUBMITTED) in
 *  both the review-per-company panel and the responder's read-only
 *  view of an IN_REVIEW request. */
export async function patchQuestionReviewStatus(
  requestId: string,
  questionId: string,
  status: 'APPROVED' | 'DENIED' | 'IN_REVIEW' | 'NOT_SUBMITTED',
): Promise<unknown> {
  return apiFetch<unknown>(
    `/v1/responses/requests/${requestId}/questions/${questionId}`,
    { method: 'PATCH', body: JSON.stringify({ status }) },
    QUESTIONNAIRE_SERVICE_URL,
  );
}
